import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiActivity, FiUsers, FiAlertTriangle, FiCreditCard, FiRefreshCw,
  FiTrendingUp, FiTrendingDown, FiClock,
} from "react-icons/fi";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../utils/api";

const NAVY = "#102C67";
const TEAL = "#00A492";
const AMBER = "#D97706";
const RED = "#DC2626";

const phaseLabel = {
  upcoming: { label: "À venir", color: "bg-gray-100 text-gray-600" },
  build_up: { label: "Montage", color: "bg-amber-100 text-amber-700" },
  show_days: { label: "Show days", color: "bg-emerald-100 text-emerald-700" },
  teardown: { label: "Démontage", color: "bg-orange-100 text-orange-700" },
  closed: { label: "Terminé", color: "bg-gray-100 text-gray-500" },
  unknown: { label: "—", color: "bg-gray-100 text-gray-400" },
};

const TABS = [
  { key: "overview", label: "Vue d'ensemble" },
  { key: "events", label: "Events" },
  { key: "tickets", label: "Tickets" },
];

const StatCard = ({ label, value, sub, icon: Icon, trend }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-start justify-between">
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-2xl font-semibold mt-1" style={{ color: NAVY }}>{value}</p>
      {sub && (
        <p className={`text-xs mt-1 flex items-center gap-1 ${trend === "down" ? "text-red-500" : "text-gray-400"}`}>
          {trend === "down" && <FiTrendingDown size={12} />}
          {trend === "up" && <FiTrendingUp size={12} />}
          {sub}
        </p>
      )}
    </div>
    {Icon && (
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${TEAL}15` }}>
        <Icon size={16} color={TEAL} />
      </div>
    )}
  </div>
);

const Card = ({ title, children, action }) => (
  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
    <div className="p-4 border-b border-gray-100 flex items-center justify-between">
      <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

const EmptyState = ({ text }) => (
  <div className="p-10 text-center text-sm text-gray-400">{text}</div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get("/dashboard/super-admin");
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const progressChartData = data?.events.map((e) => ({
    name: e.name.length > 12 ? `${e.name.slice(0, 12)}…` : e.name,
    reel: e.progress.realPercent,
    prevu: e.progress.plannedPercent,
  })) || [];

  const ticketsPieData = data
    ? [
        { name: "Cleaning ouverts", value: data.tickets.cleaning.open, color: TEAL },
        { name: "Security ouverts", value: data.tickets.security.open, color: AMBER },
      ].filter((d) => d.value > 0)
    : [];

  const ticketsCritical = data ? data.tickets.cleaning.critical + data.tickets.security.critical : 0;
  const ticketsOpen = data ? data.tickets.cleaning.open + data.tickets.security.open : 0;

  return (
    <div className="min-h-screen flex bg-gray-50">
      <Sidebar />

      <main className="flex-1 p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-2xl font-bold" style={{ color: NAVY }}>Vue globale</h1>
            <p className="text-sm text-gray-500 mt-1">Tous les events, en un coup d'œil.</p>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
          >
            <FiRefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {loading && !data ? (
          <EmptyState text="Chargement..." />
        ) : error ? (
          <div className="p-10 text-center text-sm text-red-500">{error}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <StatCard label="Events" value={data.eventsCount} icon={FiActivity} />
              <StatCard label="Stands (total)" value={data.totals.stands} icon={FiUsers} />
              <StatCard
                label="Stands à risque"
                value={data.totals.riskyStands}
                icon={FiAlertTriangle}
                sub={data.totals.riskyStands > 0 ? "à surveiller" : "aucun risque"}
                trend={data.totals.riskyStands > 0 ? "down" : null}
              />
              <StatCard
                label="Badges sur site"
                value={data.badges.ratio}
                sub={`${data.badges.inside} présents`}
                icon={FiUsers}
              />
              <StatCard
                label="Tickets critiques"
                value={ticketsCritical}
                sub={`${ticketsOpen} ouverts au total`}
                icon={FiCreditCard}
                trend={ticketsCritical > 0 ? "down" : null}
              />
            </div>

            <div className="flex items-center gap-1 mb-6 bg-white border border-gray-100 rounded-xl p-1 w-fit">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={
                    tab === t.key
                      ? { backgroundColor: NAVY, color: "white" }
                      : { color: "#6B7280" }
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "overview" && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <Card title="Avancement réel vs prévu par event">
                    {progressChartData.length === 0 ? (
                      <EmptyState text="Aucun event" />
                    ) : (
                      <div className="p-4" style={{ height: 280 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={progressChartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                            <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} unit="%" />
                            <Tooltip
                              contentStyle={{ borderRadius: 8, border: "1px solid #F3F4F6", fontSize: 12 }}
                              formatter={(v) => [`${v}%`, undefined]}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="reel" name="Réel" fill={TEAL} radius={[4, 4, 0, 0]} />
                            <Bar dataKey="prevu" name="Prévu" fill={NAVY} radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </Card>
                </div>

                <Card title="Tickets ouverts par source">
                  {ticketsPieData.length === 0 ? (
                    <EmptyState text="Aucun ticket ouvert" />
                  ) : (
                    <div className="p-4" style={{ height: 280 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={ticketsPieData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={55}
                            outerRadius={85}
                            paddingAngle={2}
                          >
                            {ticketsPieData.map((entry, i) => (
                              <Cell key={i} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #F3F4F6", fontSize: 12 }} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </Card>
              </div>
            )}

            {tab === "events" && (
              <Card title="Events">
                {data.events.length === 0 ? (
                  <EmptyState text="Aucun event" />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 text-left text-xs text-gray-400 uppercase tracking-wide">
                          <th className="px-4 py-3 font-medium">Event</th>
                          <th className="px-4 py-3 font-medium">Phase</th>
                          <th className="px-4 py-3 font-medium text-right">Stands</th>
                          <th className="px-4 py-3 font-medium text-right">Avancement</th>
                          <th className="px-4 py-3 font-medium text-right">À risque</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.events.map((event) => {
                          const phase = phaseLabel[event.phase] || phaseLabel.unknown;
                          return (
                            <tr
                              key={event.eventId}
                              onClick={() => navigate(`/events/${event.eventId}`)}
                              className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 cursor-pointer transition-colors"
                            >
                              <td className="px-4 py-3 font-medium text-gray-800">{event.name}</td>
                              <td className="px-4 py-3">
                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${phase.color}`}>
                                  {phase.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">{event.standsCount}</td>
                              <td className="px-4 py-3 text-right">
                                <span className={event.progress.status === "delayed" ? "text-red-500" : "text-gray-700"}>
                                  {event.progress.realPercent}% / {event.progress.plannedPercent}%
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                {event.riskyStandsCount > 0 ? (
                                  <span className="text-red-500 font-semibold">{event.riskyStandsCount}</span>
                                ) : (
                                  <span className="text-gray-400">0</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}

            {tab === "tickets" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {["cleaning", "security"].map((source) => {
                  const s = data.tickets[source];
                  const barData = [
                    { name: "Total", value: s.total },
                    { name: "Ouverts", value: s.open },
                    { name: "Critiques", value: s.critical },
                  ];
                  return (
                    <Card key={source} title={source === "cleaning" ? "Cleaning" : "Security"}>
                      <div className="p-4" style={{ height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={barData} layout="vertical" margin={{ left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#374151" }} width={70} />
                            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #F3F4F6", fontSize: 12 }} />
                            <Bar dataKey="value" fill={source === "cleaning" ? TEAL : AMBER} radius={[0, 4, 4, 0]} barSize={22} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;